import React, { useState, useMemo, useEffect } from "react";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import ListingTable from "../../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../../common/components/admin-pagination-component/admin-pagination-component";
import { userHeaders } from "./user-management-helper";
import editIcon from "../../../../assets/icons/edit-outlined-icon.svg";
import deleteIcon from "../../../../assets/icons/delete-outlined-red-icon.svg";
import resendMailIcon from "../../../../assets/icons/email-resend-outline.svg";
import alertIcon from "../../../../assets/icons/alert-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Dialog, DialogContent, IconButton, Select, MenuItem, FormControl } from "@mui/material";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import ActionConfirmationPopup from "../../../../common/components/action-confirmation-popup/action-confirmation-popup";
import SearchInputComponent from "../../../../common/components/search-input-component/search-input-component";
import EditUserModal from "./edit-user-modal";
import AddUserModal from "./add-user-modal";
import { getUsersList, updateUserStatus, deleteUser, sendTemporaryPassword } from "../../../../api/admin/user-mgmt.api";
import "./user-management.scss";

const UserManagement: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("All Roles");
    const [currentPage, setCurrentPage] = useState(1);
    const [userData, setUserData] = useState<any[]>([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isTempPasswordModalOpen, setIsTempPasswordModalOpen] = useState(false);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [infoPopupMessage, setInfoPopupMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const pageSize = 10;

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params: any = {
                timezone: "Asia/Calcutta",
                page: currentPage,
                limit: pageSize,
            };

            if (searchTerm) {
                params.searchText = searchTerm;
            }

            if (roleFilter !== "All Roles") {
                params.filterByRole = roleFilter;
            }

            const response = await getUsersList(params);
            if (response.status === 200) {
                const data = response.data?.data;
                setUserData(data?.users || []);
                setTotalCount(data?.totalCount || 0);
            }
        } catch (error: any) {
            console.error("Error fetching users:", error);
            setInfoPopupMessage("Failed to fetch users.");
            setShowInfoPopup(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [currentPage, roleFilter]);

    // Use a debounce for search to avoid excessive API calls
    useEffect(() => {
        const handler = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchUsers();
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Server-side filtering and pagination, no need for client-side useMemo here
    const paginatedData = userData;

    const handleEdit = (user: any) => {
        setCurrentUser(user);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (user: any) => {
        setCurrentUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleStatusToggle = (user: any) => {
        setCurrentUser(user);
        const isActive = user.status === "Active" || user.isActive;
        if (isActive) {
            setIsStatusModalOpen(true);
        } else {
            confirmStatusChange(user);
        }
    };

    const handleTempPasswordClick = (user: any) => {
        setCurrentUser(user);
        setIsTempPasswordModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!currentUser) return;
        try {
            const response = await deleteUser(currentUser.id);
            if (response.status === 200 || response.data?.success) {
                setUserData(userData.filter(u => u.id !== currentUser.id));
                setTotalCount(prev => prev - 1);
                setIsDeleteModalOpen(false);
                setInfoPopupMessage("User deleted successfully.");
                setShowInfoPopup(true);
            }
        } catch (error: any) {
            console.error("Error deleting user:", error);
            setInfoPopupMessage(error?.response?.data?.message || "Failed to delete user.");
            setShowInfoPopup(true);
        }
    };

    const confirmStatusChange = async (userToUpdate?: any) => {
        const user = userToUpdate || currentUser;
        if (!user || user.id === undefined) {
            setInfoPopupMessage("User ID is missing.");
            setShowInfoPopup(true);
            return;
        }

        // Determine if we are activating or deactivating
        // If from toggle, we flip the current status.
        // If from edit modal, we use the status from editedUser (if passed).
        // But the user just wants the confirmStatusChange to work.

        let targetActiveStatus: string;
        if (userToUpdate && userToUpdate.status) {
            // If passed from edit modal (userToUpdate is editedUser)
            targetActiveStatus = userToUpdate.status === "Active" ? "true" : "false";
        } else {
            // If from toggle popup, we use currentUser's current status and flip it
            const currentIsActive = currentUser.status === "Active" || currentUser.isActive;
            targetActiveStatus = currentIsActive ? "false" : "true";
        }

        try {
            const params = new URLSearchParams();
            params.append("userId", user.id.toString());
            params.append("activeStatus", targetActiveStatus);

            const response = await updateUserStatus(params);
            if (response.status === 200 || response.data?.success) {
                const isNowActive = targetActiveStatus === "true";
                setUserData(prev => prev.map(u =>
                    u.id === user.id
                        ? { ...u, status: isNowActive ? "Active" : "Inactive", isActive: isNowActive }
                        : u
                ));
                setIsStatusModalOpen(false);
                setInfoPopupMessage(`User ${isNowActive ? "activated" : "deactivated"} successfully.`);
                setShowInfoPopup(true);
                if (!userToUpdate) setCurrentUser(null);
            }
        } catch (error: any) {
            console.error("Error updating user status:", error);
            let errorMessage = "Failed to update user status.";

            const serverErrors = error?.response?.data?.errors;
            if (serverErrors && Array.isArray(serverErrors)) {
                // Handle both [{userId: "msg"}] and ["msg"] or similar
                const firstError = serverErrors[0];
                if (typeof firstError === 'object' && firstError !== null) {
                    const firstKey = Object.keys(firstError)[0];
                    if (firstKey) errorMessage = firstError[firstKey];
                } else if (typeof firstError === 'string') {
                    errorMessage = firstError;
                }
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            setInfoPopupMessage(errorMessage);
            setShowInfoPopup(true);
        }
    };

    const handleSaveEdit = async (editedUser: any) => {
        const originalStatus = currentUser.status || (currentUser.isActive ? "Active" : "Inactive");

        if (editedUser.status !== originalStatus) {
            if (editedUser.status === "Inactive") {
                // Trigger the confirmation popup for deactivation
                // We keep currentUser as it is (it has the correct ID)
                setIsEditModalOpen(false);
                setIsStatusModalOpen(true);
            } else {
                // Direct API call for activation
                await confirmStatusChange(editedUser);
                setIsEditModalOpen(false);
            }
        } else {
            setIsEditModalOpen(false);
        }
    };

    const confirmSendTempPassword = async () => {
        if (!currentUser) return;
        try {
            const params = new URLSearchParams();
            params.append("userId", currentUser.id.toString());
            const response = await sendTemporaryPassword(params);
            if (response.status === 200 || response.data?.success) {
                setIsTempPasswordModalOpen(false);
                setInfoPopupMessage("Temporary password sent successfully.");
                setShowInfoPopup(true);
            }
        } catch (error: any) {
            console.error("Error sending temporary password:", error);
            setInfoPopupMessage(error?.response?.data?.message || "Failed to send temporary password.");
            setShowInfoPopup(true);
        }
    };

    const handleAddClick = () => {
        setIsAddModalOpen(true);
    };

    const handleSaveAdd = (user: any) => {
        const userToAdd = {
            ...user,
            id: Math.max(...userData.map(u => u.id), 0) + 1,
            createdDate: new Date().toLocaleDateString('en-GB'),
        };
        setUserData([userToAdd, ...userData]);
        setIsAddModalOpen(false);
        setInfoPopupMessage("User added successfully.");
        setShowInfoPopup(true);
    };

    const formattedTableData = paginatedData.map(user => {
        const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || "-";
        const userStatus = user.status || (user.isActive ? "Active" : "Inactive");
        const userRole = user.role || "";

        return {
            ...user,
            name: userName,
            createdDate: user.createdAt ? (() => {
                const [m, d, y] = user.createdAt.split(" ")[0].split("-");
                return `${d}/${m}/${y}`;
            })() : "-",
            role: (
                <span className={`role-chip ${userRole.toLowerCase().replace(" ", "")}`}>
                    {userRole || "N/A"}
                </span>
            ),
            status: (
                <span
                    className={`status-chip ${userStatus.toLowerCase()}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleStatusToggle(user);
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {userStatus}
                </span>
            ),
            action: (
                <div className="action-buttons">
                    {userRole !== "Super Admin" && (
                        <>
                            {(userRole === "Preneed Planner" || userRole === "Provider") && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTempPasswordClick(user);
                                    }}
                                >
                                    <img src={resendMailIcon} alt="Resend Password" className="resend-password-icon" />
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(user);
                                }}
                            >
                                <img src={editIcon} alt="Edit" className="edit-user-icon" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(user);
                                }}
                            >
                                <img src={deleteIcon} alt="Delete" style={{ width: 18 }} />
                            </IconButton>
                        </>
                    )}
                </div>
            )
        };
    });

    return (
        <div className="user-management-wrapper">
            <ProfileHeader
                breadcrumbLink={[
                    { label: "User Management" },
                ]}
            />

            <div className="user-list-content-wrapper">
                <div className="user-list-filters">
                    <div className="left-filters">
                        <div className="search-wrapper">
                            <SearchInputComponent
                                placeholder="Search by name or email..."
                                onSearch={(value: string) => setSearchTerm(value)}
                            />
                        </div>
                        <FormControl size="small" className="role-filter-form">
                            <Select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value as string)}
                                displayEmpty
                                className="role-select"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root.Mui-selected": {
                                                backgroundColor: "#104145 !important",
                                                color: "white",
                                            },
                                            "& .MuiMenuItem-root.Mui-selected:hover": {
                                                backgroundColor: "#0d3539 !important",
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="All Roles">All Roles</MenuItem>
                                <MenuItem value="Provider">Provider</MenuItem>
                                <MenuItem value="User">User</MenuItem>
                                <MenuItem value="Preneed Planner">Preneed Planner</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="resend-credentials-info">
                        <div className="icon-circle">
                            <img src={resendMailIcon} alt="Resend" className="resend-password-icon" />
                        </div>
                        <span className="info-text">Resend User Credentials Option</span>
                    </div>
                </div>

                {formattedTableData.length > 0 ? <div className="user-table-card">
                    <ListingTable
                        headers={userHeaders}
                        data={formattedTableData}
                        noResultsText={loading ? "Loading users..." : ""}
                        fieldName="user-management"
                    />
                </div> : <div className="no-users-found">No users found.</div>}

                {totalCount > 0 && (
                    <AdminPaginationComponent
                        totalRecords={totalCount}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onPageChange={(e, value) => setCurrentPage(value)}
                    />
                )}
            </div>

            {/* Edit User Modal */}
            <EditUserModal
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={handleSaveEdit}
                user={currentUser}
            />

            {/* Add User Modal */}
            <AddUserModal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSave={handleSaveAdd}
            />

            <ActionConfirmationPopup
                open={isDeleteModalOpen}
                title="Delete User"
                note={
                    currentUser?.firstName || currentUser?.lastName
                        ? `Are you sure you want to delete ${currentUser?.firstName || ''} ${currentUser?.lastName || ''}? This action cannot be undone.`
                        : "Are you sure you want to delete this user? This action cannot be undone."
                }
                confirmButtonText="Delete User"
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                variant="danger"
            />

            <ActionConfirmationPopup
                open={isStatusModalOpen}
                title={(currentUser?.status === "Active" || currentUser?.isActive) ? "Deactive User" : "Activate User"}
                note={(currentUser?.status === "Active" || currentUser?.isActive)
                    ? "Are you sure you want to deactive this user?"
                    : "Are you sure you want to activate this user?"}
                confirmButtonText="Confirm"
                onClose={() => setIsStatusModalOpen(false)}
                onConfirm={() => confirmStatusChange()}
                variant={(currentUser?.status === "Active" || currentUser?.isActive) ? "danger" : "success"}
                showIcon={false}
            />

            <ActionConfirmationPopup
                open={isTempPasswordModalOpen}
                title="Send Temporary Password"
                note={`Are you sure you want to send a temporary password to ${currentUser?.name ? currentUser?.name : currentUser?.email || 'this user'}?`}
                confirmButtonText="Confirm"
                onClose={() => setIsTempPasswordModalOpen(false)}
                onConfirm={confirmSendTempPassword}
                variant="success"
                showIcon={false}
            />

            <InfoPopup
                open={showInfoPopup}
                note={infoPopupMessage}
                onClose={() => setShowInfoPopup(false)}
            />
        </div>
    );
};

export default UserManagement;
